// src/components/PatientAuditTimeline.jsx
//
// Read-only timeline of audit events for a single patient.
// Reads from patient_audits/{patientId}/events ordered by createdAt desc.
// This component never writes — it has no edit or delete affordances.

import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { getPatientAuditEventsPath } from "../services/firestorePaths";
import {
  ChevronDown,
  Eye,
  Pencil,
  AlertTriangle,
  ShieldCheck,
  History,
  Clock,
  User,
  ArrowRight,
} from "lucide-react";

const TYPE_STYLES = {
  READ: {
    chip: "bg-sky-400/15 text-sky-200 border-sky-400/30",
    Icon: Eye,
  },
  WRITE: {
    chip: "bg-purple-400/15 text-purple-200 border-purple-400/30",
    Icon: Pencil,
  },
};

const SEVERITY_STYLES = {
  info: "text-slate-300",
  warning: "text-amber-300",
  critical: "text-rose-300",
};

const formatTimestamp = (ts) => {
  if (!ts) return "—";
  try {
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
    if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
    return new Date(ts).toLocaleString();
  } catch {
    return "—";
  }
};

const formatValue = (val) => {
  if (val === null || val === undefined) return "—";
  if (Array.isArray(val))
    return val.length ? val.map((v) => formatValue(v)).join(", ") : "—";
  if (typeof val === "object") {
    try {
      return JSON.stringify(val);
    } catch {
      return "[object]";
    }
  }
  if (val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return String(val);
};

const PatientAuditTimeline = ({ patientId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientId) {
      setEvents([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, getPatientAuditEventsPath(patientId)),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        setLoading(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error("Audit timeline subscribe error:", err);
        setError(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [patientId]);

  const toggle = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.04] via-transparent to-blue-500/[0.04] pointer-events-none" />

      <div className="relative px-3 py-2.5 border-b border-white/5 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
          <History className="w-3.5 h-3.5 text-purple-300" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">
            Audit Trail
          </h3>
          <p className="text-[10px] text-slate-400 leading-tight">
            Read-only · Centralized
          </p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 text-[10px] font-mono text-slate-200">
          {events.length}
        </span>
      </div>

      <div className="relative max-h-[480px] overflow-y-auto scrollbar-thin">
        {loading ? (
          <div className="p-4 text-[11px] text-slate-400 text-center">
            Loading audit events…
          </div>
        ) : error ? (
          <div className="p-4 text-[11px] text-rose-300 text-center">
            Failed to load audit trail.
          </div>
        ) : events.length === 0 ? (
          <div className="p-6 text-center">
            <div className="mx-auto w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-2">
              <ShieldCheck className="w-4 h-4 text-purple-300" />
            </div>
            <p className="text-xs text-white font-semibold">No audit events</p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Events will appear here as actions occur.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {events.map((ev) => {
              const ts = TYPE_STYLES[ev.type] || TYPE_STYLES.WRITE;
              const Icon = ts.Icon;
              const isOpen = !!expanded[ev.id];
              const changes = Array.isArray(ev.changes) ? ev.changes : [];
              const changeCount = changes.length;
              const severityCls =
                SEVERITY_STYLES[ev.severity] || SEVERITY_STYLES.info;
              return (
                <li key={ev.id} className="p-3 hover:bg-white/[0.03] transition">
                  <button
                    type="button"
                    onClick={() => toggle(ev.id)}
                    className="w-full flex items-start gap-2 text-left group"
                  >
                    <div className="w-7 h-7 rounded-md bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
                      <Icon className="w-3.5 h-3.5 text-purple-200" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-white truncate">
                          {ev.title || ev.action || "Event"}
                        </span>
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold border ${ts.chip}`}
                        >
                          {ev.type || "WRITE"}
                        </span>
                        {ev.severity && ev.severity !== "info" && (
                          <span
                            className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold ${severityCls}`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" />
                            {ev.severity}
                          </span>
                        )}
                        {changeCount > 0 && (
                          <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/[0.04] border border-white/10 text-[9px] text-slate-300">
                            {changeCount} change{changeCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 truncate max-w-[160px]">
                          <User className="w-2.5 h-2.5 shrink-0" />
                          <span className="truncate">
                            {ev.actorEmail || "unknown"}
                          </span>
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />
                          {formatTimestamp(ev.createdAt)}
                        </span>
                        {ev.source && (
                          <span className="font-mono text-[10px] text-slate-500">
                            src: {ev.source}
                          </span>
                        )}
                      </div>
                    </div>
                    {changeCount > 0 && (
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 mt-1 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </button>

                  {isOpen && changeCount > 0 && (
                    <div className="mt-2 ml-9 rounded-lg border border-white/10 bg-white/[0.025] divide-y divide-white/5">
                      {changes.map((c, i) => (
                        <div key={i} className="px-2.5 py-1.5 text-[11px]">
                          <div className="text-[9px] uppercase tracking-wider text-purple-300 font-semibold mb-0.5">
                            {c.field}
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-rose-400/10 border border-rose-400/20 text-rose-200">
                              <span className="text-[9px] uppercase tracking-wider opacity-70">
                                From
                              </span>
                              <span className="max-w-[180px] truncate text-[11px]">
                                {formatValue(c.from)}
                              </span>
                            </span>
                            <ArrowRight className="w-3 h-3 text-slate-500 shrink-0" />
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-400/10 border border-emerald-400/20 text-emerald-200">
                              <span className="text-[9px] uppercase tracking-wider opacity-70">
                                To
                              </span>
                              <span className="max-w-[180px] truncate text-[11px]">
                                {formatValue(c.to)}
                              </span>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PatientAuditTimeline;
